import { request, requestFormData } from './client';

export type ProductStatus = 'ACTIVE' | 'ARCHIVED';

export interface ProductImage {
  id: number;
  productId: number;
  fileName: string;
  publicUrl: string;
  sortOrder?: number;
  createdAt?: string | null;
}

export interface Product {
  id: number;
  name: string;
  description?: string | null;
  features?: string | null;
  price?: string | null;
  targetAudience?: string | null;
  pinOwnReplyEnabled?: boolean;
  commentBrief?: string | null;
  status: ProductStatus;
  createdAt?: string | null;
  updatedAt?: string | null;
  /** 목록 API는 사진 URL 없이 장수만 내려줍니다. */
  imageCount?: number;
  images?: ProductImage[];
}

export interface ProductUpsertRequest {
  name: string;
  description?: string;
  features?: string;
  price?: string;
  targetAudience?: string;
  pinOwnReplyEnabled?: boolean;
  commentBrief?: string;
}

export interface ProductGenerateRequest {
  threadAccountIds: number[];
  includeImage?: boolean | null;
}

export interface ProductGenerateResponse {
  productId: number;
  productName: string;
  enqueuedCount: number;
  reservations: Array<{
    id: number;
    threadAccountId: number;
    productId?: number | null;
    subject: string;
    status: string;
    source: string;
  }>;
}

export function listProducts() {
  return request<Product[]>('/api/products', { method: 'GET' });
}

export function getProduct(id: number) {
  return request<Product>(`/api/products/${id}`, { method: 'GET' });
}

export function createProduct(payload: ProductUpsertRequest) {
  return request<Product>('/api/products', { method: 'POST', body: payload });
}

export function updateProduct(id: number, payload: ProductUpsertRequest) {
  return request<Product>(`/api/products/${id}`, {
    method: 'PUT',
    body: payload,
  });
}

export function archiveProduct(id: number) {
  return request<string>(`/api/products/${id}/archive`, { method: 'POST' });
}

export function deleteProduct(id: number) {
  return request<string>(`/api/products/${id}`, { method: 'DELETE' });
}

export function enqueueProductPosts(
  id: number,
  payload: ProductGenerateRequest
) {
  return request<ProductGenerateResponse>(`/api/products/${id}/enqueue`, {
    method: 'POST',
    body: payload,
  });
}

export function listProductImages(id: number) {
  return request<ProductImage[]>(`/api/products/${id}/images`, {
    method: 'GET',
  });
}

export function uploadProductImages(id: number, files: File[]) {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  return requestFormData<ProductImage[]>(`/api/products/${id}/images`, formData);
}

export function deleteProductImage(productId: number, imageId: number) {
  return request<string>(`/api/products/${productId}/images/${imageId}`, {
    method: 'DELETE',
  });
}

export interface ProductAccountAssignment {
  productId: number;
  threadAccountIds: number[];
}

export function listProductAssignments(productId: number) {
  return request<ProductAccountAssignment>(
    `/api/products/${productId}/assignments`,
    { method: 'GET' }
  );
}

export function assignProductAccount(productId: number, threadAccountId: number) {
  return request<ProductAccountAssignment>(
    `/api/products/${productId}/assignments/${threadAccountId}`,
    { method: 'POST' }
  );
}

export function unassignProductAccount(
  productId: number,
  threadAccountId: number
) {
  return request<ProductAccountAssignment>(
    `/api/products/${productId}/assignments/${threadAccountId}`,
    { method: 'DELETE' }
  );
}

export interface ProductCrawlCandidate {
  source: string;
  sourceUrl: string;
  title?: string | null;
  snippet?: string | null;
  username?: string | null;
  alreadyExists?: boolean;
}

export interface ProductCrawlPreviewResponse {
  keyword: string;
  candidates: ProductCrawlCandidate[];
  hint?: string | null;
}

export interface ProductCrawlImportResponse {
  createdCount: number;
  skippedCount: number;
  products: Product[];
}

export function previewProductCrawl(payload: {
  keyword: string;
  threadAccountId?: number | null;
  limit?: number;
}) {
  return request<ProductCrawlPreviewResponse>('/api/products/crawl/preview', {
    method: 'POST',
    body: payload,
  });
}

export function importProductCrawl(payload: {
  keyword: string;
  threadAccountId?: number | null;
  limit?: number;
  selectedSourceUrls: string[];
  candidates?: ProductCrawlCandidate[];
}) {
  return request<ProductCrawlImportResponse>('/api/products/crawl/import', {
    method: 'POST',
    body: payload,
  });
}
